import { DateTime } from 'luxon'
import { snakeCase } from 'change-case'
import * as React from 'react'
import { SegmentContext, useSegmentContext } from './SegmentProvider'

// Normalize every event property name to snake_case (e.g. "Job ID" -> "job_id", "deviceCategory" ->
// "device_category") so events land with clean, consistent column names downstream instead of
// spaced/mixed-case keys. Applied centrally to every tracked event; values are left untouched.
const normalizeEventProperties = (properties: Record<string, any> = {}): Record<string, any> => {
  const normalized: Record<string, any> = {}
  Object.keys(properties).forEach((key) => {
    normalized[snakeCase(key)] = properties[key]
  })
  return normalized
}

type Props = {
  // eslint-disable-next-line @typescript-eslint/ban-types
  children: Function | React.ReactNode
  // eslint-disable-next-line @typescript-eslint/ban-types
  eventProperties?: object | Function
}

export function useSegment(eventProperties: object = {}) {
  const { segmentClient, eventProperties: inheritedProperties } = useSegmentContext()
  return React.useMemo(() => {
    function logEvent(eventType, eventPropertiesIn: object = {}, callback?: any) {
      if (!segmentClient) {
        return
      }

      let computed = inheritedProperties
      if (typeof eventProperties === 'function') {
        computed = eventProperties(computed)
      } else {
        computed = { ...computed, ...(eventProperties || {}) }
      }
      if (typeof eventPropertiesIn === 'function') {
        computed = eventPropertiesIn(computed)
      } else {
        computed = { ...computed, ...(eventPropertiesIn || {}) }
      }

      // event_time
      computed = { ...computed, event_time: DateTime.now().toSeconds() }

      segmentClient.track(eventType, normalizeEventProperties(computed), callback)
    }

    // eslint-disable-next-line @typescript-eslint/ban-types
    function instrument<T extends Function>(eventType: string, func: T): T {
      function fn(...params: any) {
        const retVal = func ? func(...params) : undefined
        logEvent(eventType)
        return retVal
      }
      return fn as any
    }

    return {
      logEvent: logEvent,
      instrument: instrument,
      eventProperties: inheritedProperties,
      segmentClient: segmentClient,
    }
  }, [eventProperties, inheritedProperties, segmentClient])
}

export function Segment(props: Props) {
  const { logEvent, instrument, eventProperties, segmentClient } = useSegment(undefined)

  // Merge this <Segment>'s properties onto whatever it inherited, and expose them to descendants
  // via context so their logEvent/LogOnMount calls pick them up (this is how e.g. the
  // Self-Scheduling flow supplies Job ID and vehicle/market props to its page-view events).
  const value = React.useMemo(
    () => ({
      eventProperties: { ...eventProperties, ...(props.eventProperties || {}) },
      segmentClient,
    }),
    [eventProperties, props.eventProperties, segmentClient]
  )

  const renderChildren = () =>
    typeof props.children === 'function' ? props.children({ logEvent, instrument }) : props.children || null

  // If we're not providing any additional properties, just get out of the way and call the component
  if (!eventProperties) {
    return renderChildren()
  }

  return <SegmentContext.Provider value={value}>{renderChildren()}</SegmentContext.Provider>
}
